import React, { useState } from 'react';
import { Col, Image, Row } from 'react-bootstrap';
import { GiAirplaneDeparture } from 'react-icons/gi';
import { useTranslation } from 'react-i18next';
import { IOffer } from '../../../../../../../../model/offer/IOffer';
import { CustomImageHandler } from '../../../../../../../../shared/components/CustomImageHandler';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { Totals } from '../offer/Totals';
import { calcFee, formatDate, getTotals } from '../../../../../../../../shared/functions/Functions';
import { HistoryAdditionalItemsTable } from './HistoryAdditionalItemsTable';
import { HistoryOfferPlatformTable } from './HistoryOfferPlatformTable';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { TotalTypes } from '../../../../../../../../enum/TotalTypes';
import { InfluencerFeeTotals } from '../../../../../../../../shared/components/offer/InfluencerFeeTotals';
import { getTruncatedData } from '../../../../../../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../../../../../../shared/components/tooltip/Tooltip';
import FilePlaceholder from '../../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../../shared/components/Fancybox';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import { downloadFile } from '../../../../../../../../shared/functions/Functions';
import ItemValueSection from '../../../../../../../../shared/components/offer/ItemValueSection';
import { priceSplitter } from '../../../../../../../../utils/helpers/priceSplitter';
interface INegotiationHistoryDetailProps {
    offer?: IOffer;
    isCharity?: boolean;
}

const HistoryDetail = ({ offer, isCharity }: INegotiationHistoryDetailProps) => {
    const [expended, setExpended] = useState(false);
    const { t } = useTranslation();
    const { isTransparentPricing, campaign } = useSelector((state: IAllStates) => state.offerBrand);
    const itemValue = campaign ? campaign?.charityValueDesc : (offer as any)?.campaign?.charityValueDesc;
    const brandFeePercentage = Math.round((1 - Number(campaign?.feePercentage ?? 0.0)) * 100);
    const subTotalSum = getTotals(offer?.additionalOfferItems, offer?.totalPlatforms);
    const grandTotalForDisplay = isTransparentPricing ? offer?.totalWithFee : subTotalSum;
    const legalDocument = offer?.legalDocuments?.[0];
    const { truncatedName: truncatedFileName, isTruncated: isFileNameTruncated } = getTruncatedData(
        legalDocument?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderName, isTruncated: isUploaderNameTruncated } = getTruncatedData(
        legalDocument?.uploadedBy?.displayName ?? '',
        30
    );
    const influencerFeePercent = offer?.influencerFeePercentage;
    const hasInfluencerFee = offer?.hasInfluencerFee;
    const isIvConnectedClub = offer?.isIvConnectedClub;
    const influenceFeeSum = hasInfluencerFee ? calcFee(subTotalSum ?? 0, influencerFeePercent) : 0;

    const grandTotalMinusInfluencerFee = Math.round((subTotalSum ?? 0) - influenceFeeSum);

    return (
        <>
            <Accordion aria-expanded={expended} className="mt-1" expanded={expended}>
                <AccordionSummary className="p-0 border-0" onClick={() => setExpended(!expended)}>
                    <Row className="default-radius bg-light py-2 px-4">
                        <Col lg={6} className="d-flex align-items-center px-0">
                            {offer?.counterOfferBy ? (
                                <CustomImageHandler
                                    altTag={'User Profile'}
                                    classes="rounded-img-sm"
                                    thumbnailPath={
                                        offer?.counterOfferBy.isSupervisor
                                            ? ''
                                            : offer?.counterOfferBy?.profilePhoto?.thumbnail
                                    }
                                    photoPath={
                                        offer?.counterOfferBy.isSupervisor
                                            ? ''
                                            : offer?.counterOfferBy?.profilePhoto?.thumbnail
                                    }
                                    name={
                                        offer?.counterOfferBy?.displayName?.trim() ||
                                        `${offer?.counterOfferBy?.firstName ?? ''} ${
                                            offer?.counterOfferBy?.lastName ?? ''
                                        }`.trim()
                                    }
                                />
                            ) : (
                                <CustomImageHandler
                                    altTag={'User Profile'}
                                    classes="rounded-img-sm"
                                    thumbnailPath={offer?.user?.profilePhoto?.thumbnail}
                                    photoPath={offer?.user?.profilePhoto?.thumbnail}
                                    name={
                                        offer?.user?.displayName?.trim() ||
                                        `${offer?.user?.firstName ?? ''} ${offer?.user?.lastName ?? ''}`.trim()
                                    }
                                />
                            )}
                            <div>
                                {offer?.counterOfferBy ? (
                                    <h6 className="ml-3 mb-1">
                                        {offer?.counterOfferBy.isSupervisor
                                            ? 'Customer Success by i.v.'
                                            : !!offer?.counterOfferBy?.displayName
                                            ? offer?.counterOfferBy?.displayName
                                            : offer?.counterOfferBy?.firstName + ' ' + offer?.counterOfferBy?.lastName}
                                    </h6>
                                ) : (
                                    <h6 className="ml-3 mb-1">
                                        {!!offer?.user.displayName
                                            ? offer?.user.displayName
                                            : offer?.user?.firstName + ' ' + offer?.user?.lastName}
                                    </h6>
                                )}
                                <h6 className="ml-3 mb-0">
                                    {formatDate(offer?.createdAt ?? '')} - {offer?.createdAt.substring(11, 16)}
                                </h6>
                            </div>
                        </Col>
                        <Col lg={6} className="d-flex align-items-center justify-content-between py-3 px-0 p-lg-0 ">
                            <div>
                                <div className="d-flex align-items-center">
                                    <h5 className="mb-0 text-primary euro-symbol">
                                        {priceSplitter(
                                            Math.round(
                                                (hasInfluencerFee
                                                    ? grandTotalMinusInfluencerFee
                                                    : grandTotalForDisplay) ?? 0
                                            )
                                        )}
                                    </h5>
                                </div>
                                {!!offer?.additionalOfferItems?.length && (
                                    <span className="text-muted font-10">
                                        <GiAirplaneDeparture className="text-primary mr-1 font-14" />
                                        {t('brand.campaign.book.offer.additional_costs_included')}
                                    </span>
                                )}
                            </div>
                            {expended ? (
                                <div className="text-uppercase text-muted d-flex align-items-center">
                                    <p className="mb-0">{t('general.button.hide_details')}</p>
                                    <Image className="ml-2" alt="image-up" src="/icons/img_arrow_up.svg" />
                                </div>
                            ) : (
                                <div className="text-uppercase text-muted d-flex align-items-center">
                                    <p className="mb-0">{t('general.button.see_details')}</p>
                                    <Image className="ml-2" alt="image-down" src="/icons/img_arrow_down.svg" />
                                </div>
                            )}
                        </Col>
                    </Row>
                </AccordionSummary>
                <AccordionDetails className="p-0 ">
                    <HistoryOfferPlatformTable totalPlatforms={offer?.totalPlatforms} />
                    {offer?.additionalOfferItems?.length !== 0 && (
                        <>
                            <HistoryAdditionalItemsTable additionalItems={offer?.additionalOfferItems} />
                            <Totals
                                title={TotalTypes.ADDITIONAL_ITEMS}
                                price={getTotals(offer?.additionalOfferItems)}
                                displayPlus={true}
                            />
                        </>
                    )}
                    {isTransparentPricing && !isCharity && (
                        <div className="w-100 mt-3">
                            <Totals
                                title={TotalTypes.SUBTOTAL}
                                price={subTotalSum}
                                feePercentage={100 - brandFeePercentage}
                            />
                        </div>
                    )}
                    {isTransparentPricing && (
                        <div className="w-100 mt-3">
                            <Totals
                                title={TotalTypes.FEE}
                                price={offer?.feeAmount ?? 0}
                                feePercentage={brandFeePercentage}
                                displayPlus={true}
                            />
                        </div>
                    )}
                    {(!isCharity || (isCharity && grandTotalMinusInfluencerFee > 0)) && (
                        <InfluencerFeeTotals
                            subTotalAndAddons={subTotalSum ?? 0}
                            serviceFeeSum={Math.round(influenceFeeSum)}
                            containerClassName="pt-3"
                            grandTotalSum={grandTotalMinusInfluencerFee}
                            feePercentage={influencerFeePercent}
                            hasFee={hasInfluencerFee}
                            isIvConnectedClub={isIvConnectedClub}
                        />
                    )}
                    {!hasInfluencerFee && (
                        <div className="mt-3">
                            <Totals
                                title={TotalTypes.GRAND}
                                price={grandTotalForDisplay}
                                feePercentage={isTransparentPricing ? 100 : undefined}
                            />
                        </div>
                    )}
                    {isCharity && itemValue && <ItemValueSection description={itemValue} />}
                    {offer?.personalOffer && (
                        <div className="mt-4">
                            <div>
                                <h6>{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                            </div>
                            <p className="text-muted mb-0">{offer?.personalOffer}</p>
                        </div>
                    )}

                    {legalDocument && (
                        <div className="mt-3">
                            <div
                                className="d-flex align-items-start p-2 border rounded position-relative"
                                style={{ maxWidth: 300 }}
                            >
                                <Fancybox>
                                    <FilePlaceholder
                                        file={legalDocument}
                                        hideText={true}
                                        classIcon="font-22"
                                        styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                        nameFancybox="legal-document"
                                        handleDownload={() =>
                                            downloadFile(legalDocument?.path ?? '', legalDocument?.originalName)
                                        }
                                        right={-10}
                                    />
                                </Fancybox>
                                <div className="ms-2">
                                    <TooltipOverlay
                                        content={isFileNameTruncated ? legalDocument?.originalName : ''}
                                        dFlex={false}
                                    >
                                        <p
                                            className="mb-0 fw-semibold cursor-pointer"
                                            onClick={() =>
                                                NativeFancybox.show([
                                                    {
                                                        src: legalDocument?.path,
                                                        type: 'iframe',
                                                    },
                                                ])
                                            }
                                        >
                                            {truncatedFileName || '-'}
                                        </p>
                                    </TooltipOverlay>
                                    <p className="mb-0 text-muted fs-12">
                                        {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                        <TooltipOverlay
                                            content={
                                                isUploaderNameTruncated ? legalDocument?.uploadedBy?.displayName : ''
                                            }
                                            dFlex={false}
                                        >
                                            <span>{truncatedUploaderName || '-'}</span>
                                        </TooltipOverlay>
                                    </p>
                                    <p className="mb-0 text-muted fs-12">
                                        {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                        {formatDate(legalDocument?.updatedAt)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default HistoryDetail;
